import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { AxiosError } from "axios";

import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { DialogNotice, DialogTextButton } from "@/components/common/ActionDialog";
import { Field } from "@/components/settings/settings-kit";
import ContentConfigForm, { createDefaultContentConfig } from "@/components/brand/content-programs/ContentConfigForm";
import SampleContentManager from "@/components/brand/content-programs/SampleContentManager";
import ProductMultiSelect from "@/components/brand/ProductMultiSelect";
import { programService } from "@/services/program.service";
import { productService } from "@/services/product.service";
import { storageService } from "@/services/storage.service";
import { getMetaAdsStatus } from "@/services/metaAds.service";
import {
  addProgramSample,
  createBrandContentProgram,
  getBrandContentProgram,
  updateBrandContentProgram,
} from "@/services/contentProgram.service";
import { validateContentFileWithConfig, type ContentProgramConfig } from "@/config/contentProgramUpload";
import { toGidProductIds, toNumericProductIds } from "@/utils/productIds";
import type { Affiliate } from "@/types/affiliate";

function errMessage(e: unknown): string {
  if (e instanceof AxiosError) {
    const d = e.response?.data as { message?: string | string[] } | undefined;
    if (d?.message) return Array.isArray(d.message) ? d.message.join(", ") : d.message;
    return e.message || "Request failed";
  }
  return e instanceof Error ? e.message : "Something went wrong";
}

/** Bones in the shape of the form cards, so nothing jumps when the program arrives. */
function FormSkeleton() {
  return (
    <div className="max-w-3xl space-y-4 animate-pulse">
      {[3, 1, 1, 4, 1].map((fields, i) => (
        <div key={i} className="rounded-lg border border-border bg-card">
          <div className="px-5 pb-3 pt-4">
            <div className="h-4 w-32 rounded bg-muted" />
          </div>
          <div className="space-y-4 px-5 pb-5">
            {Array.from({ length: fields }).map((_, j) => (
              <div key={j} className="space-y-1.5">
                <div className="h-3.5 w-24 rounded bg-muted" />
                <div className="h-9 w-full rounded-lg bg-muted" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function BrandCreateContentProgram() {
  const { programId } = useParams<{ programId?: string }>();
  const isEdit = Boolean(programId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const closeTo = isEdit ? `/brand/content-programs/${programId}` : "/brand/content-programs";

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [commissionRate, setCommissionRate] = useState("10");
  const [openToAll, setOpenToAll] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [contentConfig, setContentConfig] = useState<ContentProgramConfig>(createDefaultContentConfig());
  const [stagedSamples, setStagedSamples] = useState<File[]>([]);
  const [uploadingSamples, setUploadingSamples] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);

  const metaQuery = useQuery({ queryKey: ["meta-ads-status"], queryFn: getMetaAdsStatus });

  const programQuery = useQuery({
    queryKey: ["brand-content-program", programId],
    queryFn: () => getBrandContentProgram(programId!),
    enabled: isEdit && Boolean(programId),
  });

  const affiliatesQuery = useQuery({
    queryKey: ["content-program-affiliates-pick"],
    queryFn: async () => {
      const res = await programService.findAffiliates({ page: 1, limit: 250 });
      return res.data ?? [];
    },
    enabled: !openToAll,
  });

  const productsQuery = useQuery({
    queryKey: ["content-program-brand-products"],
    queryFn: () => productService.findAll(),
  });

  useEffect(() => {
    const row = programQuery.data;
    if (!row || !isEdit) return;
    setTitle(row.title);
    setDescription(row.description ?? "");
    setCommissionRate(String(row.commissionRate));
    setOpenToAll(row.isOpenToAllEnrolledAffiliates ?? true);
    setSelectedIds(new Set(row.allowedAffiliates?.map((a) => a.affiliateId) ?? []));
    if (row.contentConfig) setContentConfig(row.contentConfig as ContentProgramConfig);
    setSelectedProductIds(row.entitledProductIds?.length ? toGidProductIds(row.entitledProductIds) : []);
  }, [programQuery.data, isEdit]);

  const createMut = useMutation({
    mutationFn: async (body: Parameters<typeof createBrandContentProgram>[0]) => {
      const program = await createBrandContentProgram(body);
      if (!stagedSamples.length) return program;

      setUploadingSamples(true);
      let failed = 0;
      for (let i = 0; i < stagedSamples.length; i += 1) {
        const file = stagedSamples[i];
        const chk = await validateContentFileWithConfig(file, body.contentConfig);
        if (!chk.ok) {
          failed += 1;
          continue;
        }
        try {
          const mediaUrl = await storageService.uploadProgramSampleMedia(file);
          await addProgramSample(program.id, {
            mediaUrl,
            mediaType: chk.mediaType,
            thumbnailUrl: chk.mediaType === "IMAGE" ? mediaUrl : undefined,
            sortOrder: i,
          });
        } catch {
          failed += 1;
        }
      }
      setUploadingSamples(false);
      if (failed > 0) toast.error(`Program created, but ${failed} samples did not upload. Add them from Edit program`);
      return program;
    },
    onSuccess: (p) => {
      setStagedSamples([]);
      toast.success("Program created");
      void queryClient.invalidateQueries({ queryKey: ["brand-content-programs"] });
      navigate(`/brand/content-programs/${p.id}`);
    },
    onError: (e) => {
      setUploadingSamples(false);
      toast.error(errMessage(e));
    },
  });

  const updateMut = useMutation({
    mutationFn: (body: Parameters<typeof updateBrandContentProgram>[1]) => updateBrandContentProgram(programId!, body),
    onSuccess: (p) => {
      toast.success("Program saved");
      void queryClient.invalidateQueries({ queryKey: ["brand-content-programs"] });
      void queryClient.invalidateQueries({ queryKey: ["brand-content-program", p.id] });
      navigate(`/brand/content-programs/${p.id}`);
    },
    onError: (e) => toast.error(errMessage(e)),
  });

  const toggleAffiliate = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const t = title.trim();
    const rate = Number.parseFloat(commissionRate);
    if (!t) {
      toast.error("Enter a program name");
      return;
    }
    if (!Number.isFinite(rate) || rate < 0 || rate > 100) {
      toast.error("Commission must be between 0 and 100");
      return;
    }
    if (!openToAll && selectedIds.size === 0) {
      toast.error("Pick at least one affiliate, or open the program to all");
      return;
    }
    if (!contentConfig.allowedMediaTypes.length || !contentConfig.allowedAspectRatios.length) {
      toast.error("Pick at least one media type and one placement");
      return;
    }

    const body = {
      title: t,
      commissionRate: rate,
      description: description.trim() || undefined,
      isOpenToAllEnrolledAffiliates: openToAll,
      contentConfig,
      entitledProductIds: toNumericProductIds(selectedProductIds),
      ...(!openToAll ? { allowedAffiliateIds: [...selectedIds] } : {}),
    };
    if (isEdit) updateMut.mutate(body);
    else createMut.mutate(body);
  };

  const affiliates = (affiliatesQuery.data ?? []) as Affiliate[];
  const saving = createMut.isPending || updateMut.isPending || uploadingSamples;
  const connected = metaQuery.data?.connected ?? true;

  return (
    <div className="space-y-4">
      <PageHeader title={isEdit ? "Edit content program" : "New content program"} closeTo={closeTo} />

      {isEdit && programQuery.isLoading ? (
        <FormSkeleton />
      ) : (
        <form onSubmit={handleSubmit} className="max-w-3xl space-y-4">
          {!metaQuery.isLoading && !connected && (
            <DialogNotice tone="warning">
              Affiliates can submit now, but launching ads needs a connected Meta account.{" "}
              <Link to="/brand/meta-ads" className="font-medium underline-offset-4 hover:underline">
                Connect Meta
              </Link>
            </DialogNotice>
          )}

          <DashboardCard title="Details" bodyClassName="space-y-5 px-5 pb-5">
            <Field label="Name" htmlFor="cp-title">
              <Input id="cp-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Spring reels" autoComplete="off" />
            </Field>
            <Field label="Brief" htmlFor="cp-description" help="Optional. Affiliates read this before they submit">
              <Textarea
                id="cp-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Show the product in use, keep it under 30 seconds"
              />
            </Field>
            <Field label="Commission" htmlFor="cp-commission" help="What the affiliate earns on sales attributed to ads made from their content">
              <div className="relative max-w-[10rem]">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">%</span>
                <Input
                  id="cp-commission"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  max={100}
                  step={0.5}
                  value={commissionRate}
                  onChange={(e) => setCommissionRate(e.target.value)}
                  className="pl-7 tabular-nums"
                />
              </div>
            </Field>
          </DashboardCard>

          <DashboardCard title="Affiliates" bodyClassName="space-y-4 px-5 pb-5">
            <div className="flex items-center justify-between gap-4">
              <Label htmlFor="cp-open-all" className="text-foreground">
                Open to all enrolled affiliates
              </Label>
              <Switch id="cp-open-all" checked={openToAll} onCheckedChange={setOpenToAll} />
            </div>
            {!openToAll && (
              <Field label="Who can submit" trailing={`${selectedIds.size} selected`}>
                <div className="max-h-72 overflow-y-auto rounded-lg border border-border">
                  {affiliatesQuery.isLoading ? (
                    <div className="space-y-3 p-3 animate-pulse">
                      {[0, 1, 2, 3].map((i) => (
                        <div key={i} className="h-4 w-2/3 rounded bg-muted" />
                      ))}
                    </div>
                  ) : affiliates.length === 0 ? (
                    <p className="p-3 text-sm text-muted-foreground">No enrolled affiliates yet</p>
                  ) : (
                    <ul className="divide-y divide-border">
                      {affiliates.map((a) => {
                        const id = `cp-aff-${a.id}`;
                        const name = [a.firstName, a.lastName].filter(Boolean).join(" ") || a.username;
                        return (
                          <li key={a.id}>
                            <label htmlFor={id} className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-muted/60">
                              <Checkbox id={id} checked={selectedIds.has(a.id)} onCheckedChange={() => toggleAffiliate(a.id)} />
                              <span className="min-w-0 flex-1 truncate text-foreground">{name}</span>
                              <span className="truncate text-xs text-muted-foreground">{a.email}</span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </Field>
            )}
          </DashboardCard>

          <DashboardCard title="Products" bodyClassName="px-5 pb-5">
            <Field label="Products affiliates can feature" help="Leave empty to allow every product in the store">
              <ProductMultiSelect
                products={productsQuery.data?.products ?? []}
                selectedIds={selectedProductIds}
                onChange={setSelectedProductIds}
                loading={productsQuery.isLoading}
                optional
              />
            </Field>
          </DashboardCard>

          <DashboardCard title="Content requirements" bodyClassName="px-5 pb-5">
            <ContentConfigForm value={contentConfig} onChange={setContentConfig} />
          </DashboardCard>

          <DashboardCard title="Samples" bodyClassName="px-5 pb-5">
            {isEdit && programId && programQuery.data ? (
              <SampleContentManager
                mode="persisted"
                programId={programId}
                samples={programQuery.data.samples ?? []}
                contentConfig={contentConfig}
              />
            ) : (
              <SampleContentManager
                mode="staging"
                stagedFiles={stagedSamples}
                onStagedFilesChange={setStagedSamples}
                contentConfig={contentConfig}
              />
            )}
          </DashboardCard>

          <div className="flex items-center justify-end gap-4 rounded-lg border border-border bg-card px-5 py-3">
            <DialogTextButton onClick={() => navigate(closeTo)} disabled={saving}>
              Cancel
            </DialogTextButton>
            <Button type="submit" disabled={saving}>
              {uploadingSamples ? "Uploading samples" : isEdit ? "Save program" : "Create program"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
